import {Icon} from '@iconify/react';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import BluemateTooltip from '../../controls/tooltip/bluemateTooltip';

interface Props {
    rank: number | null;
    previousRank?: number | null;
    tooltip?: string;
    isSponsored?: boolean;
    showInclusief?: boolean;
}

function getChangeClass(change: number | null | undefined): string {
    if (change !== undefined && change !== null && change !== 0) {
        return change < 0 ? 'rank-improved' : 'rank-declined';
    }
    return 'rank-neutral';
}

function getPositionClass(rank: number | null): string {
    if (!rank || rank <= 0 || rank >= 10_001) return 'rank-default';
    if (rank <= 5) return 'rank-green';
    if (rank <= 15) return 'rank-blue';
    return 'rank-red';
}

export default function RankCell(props: Props) {
    const {rank, previousRank, isSponsored, showInclusief} = props;
    const tooltip = useMemo(() => {
        if (props.tooltip) return props.tooltip;
        if ((rank ?? 0) >= 10_001) return 'Product niet aangeboden.';
    }, [props.tooltip, rank]);
    const {t} = useTranslation();
    const change = useMemo(() => {
        if (typeof previousRank === 'number' && typeof rank === 'number') {
            if (previousRank === 0) return null;
            return rank - previousRank;
        }
        return null;
    }, [previousRank, rank]);
    const displayRank = useMemo(() => {
        if (!rank || rank >= 10_001) return '\u2014';
        return rank;
    }, [rank]);
    const [hasChange, isImproved] = useMemo(() => {
        if (change === undefined || change === null) return [false, false];
        const hasChange = rank !== null && rank > 0 && rank < 10_001;
        const isImproved = hasChange && change < 0;
        return [hasChange, isImproved];
    }, [change, rank]);
    const rankClass = `${getPositionClass(rank)} ${getChangeClass(change)}`;

    return (
        <BluemateTooltip text={tooltip}>
            <div className={`rank-cell ${rankClass}`}>
                <div className='rank-value-row'>
                    <span className='rank-number'>{displayRank}</span>
                    {hasChange && (
                        <span className={`rank-delta ${isImproved ? 'improved' : 'declined'}`}>
                            <Icon
                                icon='mdi:triangle'
                                style={{
                                    fontSize: '0.5rem',
                                    transform: isImproved ? 'none' : 'rotate(180deg)'
                                }}
                            />
                            {Math.abs(change!)}
                        </span>
                    )}
                </div>
                {rank !== null && rank > 0 && (isSponsored || showInclusief) && (
                    <BluemateTooltip
                        text={
                            showInclusief
                                ? t('searchRankings.table.rankInclusiveSponsored', 'Inclusief sponsored')
                                : t('searchRankings.table.rankSponsoredPosition', 'Sponsored positie')
                        }
                    >
                        <span className='rank-sponsored-badge'>
                            <AwesomeIcon name='bullhorn' className='rank-sponsored-icon' />
                        </span>
                    </BluemateTooltip>
                )}
            </div>
        </BluemateTooltip>
    );
}
